import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { initializeMap, addMarker, setupAutocomplete, calculateRoute } from '../components/Map/services';
import { set } from 'react-hook-form';

interface MapContextType {
  mapInstance: google.maps.Map | null;
  isMapInitialized: boolean;
  initMap: (mapElement: HTMLElement, center: { lat: number; lng: number }, zoom: number) => Promise<void>;
  addMapMarker: (position: google.maps.LatLng) => google.maps.marker.AdvancedMarkerElement | undefined;
  addOrUpdateMapMarker: (position: google.maps.LatLng) => void;
  setupMapAutocomplete: (input: HTMLInputElement, onPlaceChanged: (place: google.maps.places.PlaceResult) => void) => void;
  calculateMapRoute: (origin: google.maps.LatLng, destination: google.maps.LatLng) => Promise<google.maps.DirectionsResult | undefined>;
}

const MapContext = createContext<MapContextType | undefined>(undefined);

export const MapProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [currentMarker, setCurrentMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null);

  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const initMap = useCallback(async (mapElement: HTMLElement, center: { lat: number; lng: number }, zoom: number) => {
    const loader = new Loader({
      apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
      libraries: ['places', 'marker'],
      version: 'weekly',
    });

    await loader.load();
    const map = initializeMap(mapElement, center, zoom);
    setMapInstance(map);
    setIsMapInitialized(true);
  }, []);

  const addMapMarker = useCallback((position: google.maps.LatLng) => {
    if (mapInstance) {
      return addMarker(mapInstance, position);
    }
  }, [mapInstance]);


  const setupMapAutocomplete = useCallback((input: HTMLInputElement, onPlaceChanged: (place: google.maps.places.PlaceResult) => void) => {
    if (mapInstance) {
      setupAutocomplete(mapInstance, input, onPlaceChanged);
    }
  }, [mapInstance]);

  const calculateMapRoute = useCallback(async (origin: google.maps.LatLng, destination: google.maps.LatLng): Promise<google.maps.DirectionsResult | undefined> => {
    if (mapInstance) {
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
      }

      if (infoWindowRef.current) {
        infoWindowRef.current.close(); // Close previous InfoWindow
      }

      const directionsRenderer = new google.maps.DirectionsRenderer();
      directionsRenderer.setMap(mapInstance); // Attach to the map
      directionsRendererRef.current = directionsRenderer

      const directionsService = new google.maps.DirectionsService()

      const route = await calculateRoute(directionsService, directionsRenderer, origin, destination)
      const routeLeg = route.routes[0].legs[0]
      if (routeLeg) {
        const routeInfo = `
              <div style="font-size: 14px; font-weight: bold;">
                <p>Distance: ${routeLeg?.distance?.text}</p>
                <p>Duration: ${routeLeg?.duration?.text}</p>
              </div>
            `
        const midPoint = routeLeg.steps[Math.floor(routeLeg.steps.length / 2)].end_location;

        const infoWindow = new google.maps.InfoWindow({
          content: routeInfo,
          position: midPoint,
        })
        infoWindow.open(mapInstance);
        infoWindowRef.current = infoWindow
      }
      return route
    }
    return Promise.resolve(undefined)
  }, [mapInstance]);

  const addOrUpdateMapMarker = useCallback((position: google.maps.LatLng) => {
    if (mapInstance) {
      if (currentMarker) {
        currentMarker.position = position;
      } else {
        const newMarker = addMarker(mapInstance, position);
        setCurrentMarker(newMarker);
      }
      mapInstance.panTo(position);
    }
  }, [mapInstance, currentMarker]);

  const value = {
    mapInstance,
    isMapInitialized,
    initMap,
    addMapMarker,
    addOrUpdateMapMarker,
    setupMapAutocomplete,
    calculateMapRoute,
  };

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};

export const useMapContext = () => {
  const context = useContext(MapContext);
  if (context === undefined) {
    throw new Error('useMapContext must be used within a MapProvider');
  }
  return context;
};